
import React from 'react';
import { FileUploader } from '@/components/quick-compare/FileUploader';
import { ComparisonResults } from '@/components/quick-compare/ComparisonResults';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ArrowRight } from 'lucide-react';
import { useEstimateComparison } from '@/hooks/useEstimateComparison';
import { PdfApiTest } from '@/components/quick-compare/PdfApiTest';

const QuickCompare = () => {
  const {
    status,
    originalEstimate,
    modifiedEstimate,
    comparisonResults,
    handleFileProcessed,
    startComparison,
    resetComparison,
  } = useEstimateComparison();

  return (
    <div className="container py-8 mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold tracking-tight">Quick Compare</h1>
        <p className="text-muted-foreground mt-2">
          Compare two repair estimates to identify differences in parts, labor, and pricing
        </p>
      </div>

      {/* Add the API test component at the top */}
      <PdfApiTest />

      {status === 'error' && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>
            There was an error comparing the estimates. Please try again or upload different files.
          </AlertDescription>
        </Alert>
      )}

      {(status === 'idle' || status === 'uploading' || status === 'parsing') && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FileUploader
            title="Original Estimate"
            description="Upload the baseline estimate PDF"
            onFileProcessed={(estimate) => handleFileProcessed(estimate, 'original')}
            status={status}
            setStatus={() => {}}
            isUploaded={!!originalEstimate}
          />
          <div className="hidden md:flex items-center justify-center">
            <div className="p-4 rounded-full bg-muted">
              <ArrowRight className="h-8 w-8 text-muted-foreground" />
            </div>
          </div>
          <FileUploader
            title="Modified Estimate"
            description="Upload the revised estimate PDF"
            onFileProcessed={(estimate) => handleFileProcessed(estimate, 'modified')}
            status={status}
            setStatus={() => {}}
            isUploaded={!!modifiedEstimate}
          />
        </div>
      )}
      
      {originalEstimate && modifiedEstimate && status !== 'complete' && (
        <div className="mt-6 flex justify-center">
          <button
            onClick={startComparison}
            disabled={status === 'comparing'}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            {status === 'comparing' ? 'Comparing...' : 'Compare Estimates'}
          </button>
        </div>
      )}

      {status === 'complete' && comparisonResults && (
        <>
          <ComparisonResults results={comparisonResults} />
          <div className="mt-6 flex justify-center">
            <button
              onClick={resetComparison}
              className="px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/90 transition-colors"
            >
              Compare New Estimates
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default QuickCompare;
